/**
 * ImmigrationProfile.js — Phase 3: Persistent Immigration Profile
 *
 * Features:
 * - Progressive multi-section profile form (Immigration Status, Employment, Education, Family, Prior Filings, Goals)
 * - Visual completion progress bar
 * - Personalised immigration roadmap panel
 * - Pathway eligibility insights panel
 * - Auto-save on section completion
 * - Version history viewer
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';
import { PurchaseNavButton } from './CheckoutFlow';
import { API_BASE } from '../lib/apiBase';

// ─── API Helpers ────────────────────────────────────────────────────────────

const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    const error = new Error(err.detail || 'Request failed');
    error.status = res.status;
    throw error;
  }
  return res.json();
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const SectionCard = ({ title, icon, children, completed }) => (
  <div className={`bg-gray-900 border rounded-xl p-6 mb-4 transition-all ${completed ? 'border-green-500/40' : 'border-gold/20'}`}>
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-white font-semibold text-lg flex items-center gap-2">
        <span className="text-gold">{icon}</span> {title}
      </h3>
      {completed && (
        <span className="text-green-400 text-sm flex items-center gap-1">
          <i className="fas fa-check-circle" /> Complete
        </span>
      )}
    </div>
    {children}
  </div>
);

const InputField = ({ label, value, onChange, placeholder, type = 'text', required }) => (
  <div className="mb-4">
    <label className="block text-gray-400 text-sm mb-1">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    <input
      type={type}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-[#303035]/40 border border-gold/20 rounded-lg px-4 py-2.5 text-white
                 placeholder-gray-600 focus:outline-none focus:border-gold/60 transition-colors"
    />
  </div>
);

const SelectField = ({ label, value, onChange, options, placeholder }) => (
  <div className="mb-4">
    <label className="block text-gray-400 text-sm mb-1">{label}</label>
    <select
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      className="w-full bg-[#303035]/40 border border-gold/20 rounded-lg px-4 py-2.5 text-white
                 focus:outline-none focus:border-gold/60 transition-colors appearance-none"
    >
      <option value="">{placeholder || 'Select...'}</option>
      {options.map(opt => (
        <option key={opt.value} value={opt.value} className="bg-gray-900">{opt.label}</option>
      ))}
    </select>
  </div>
);

const ProgressBar = ({ percentage }) => (
  <div className="mb-8">
    <div className="flex justify-between text-sm mb-2">
      <span className="text-gray-400">Profile Completion</span>
      <span className="text-gold font-semibold">{Math.round(percentage)}%</span>
    </div>
    <div className="w-full bg-gray-800 rounded-full h-3">
      <div
        className="h-3 rounded-full transition-all duration-500 bg-gradient-to-r from-gold to-yellow-500"
        style={{ width: `${percentage}%` }}
      />
    </div>
    {percentage < 80 && (
      <p className="text-gray-500 text-xs mt-2">
        Complete more sections for personalised pathway recommendations.
      </p>
    )}
    {percentage >= 80 && (
      <p className="text-green-400 text-xs mt-2 flex items-center gap-1">
        <i className="fas fa-check-circle" /> Strong profile — eligibility insights are now accurate.
      </p>
    )}
  </div>
);

const RoadmapPanel = ({ roadmap }) => {
  if (!roadmap) return null;

  const priorityColor = {
    high: 'text-red-400 border-red-500/30 bg-red-500/5',
    normal: 'text-gold border-gold/30 bg-gold/5',
    low: 'text-gray-400 border-gray-600/30 bg-gray-800/30',
  };

  return (
    <div className="bg-gray-900 border border-gold/20 rounded-xl p-6 mb-6">
      <h3 className="text-white font-semibold text-lg mb-1 flex items-center gap-2">
        <i className="fas fa-map-signs text-gold" /> Your Immigration Roadmap
      </h3>
      <p className="text-gray-400 text-sm mb-5">{roadmap.summary}</p>

      {roadmap.next_action && (
        <div className="bg-gold/10 border border-gold/30 rounded-lg px-4 py-3 mb-5 flex items-center justify-between">
          <span className="text-gold text-sm font-medium">
            <i className="fas fa-arrow-right mr-2" /> Next: {roadmap.next_action}
          </span>
        </div>
      )}

      <div className="space-y-3">
        {roadmap.roadmap.map(item => (
          <div
            key={item.step}
            className={`border rounded-lg px-4 py-3 ${priorityColor[item.priority] || priorityColor.normal}`}
          >
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-gold/20 text-gold text-xs flex items-center justify-center font-bold flex-shrink-0 mt-0.5">
                {item.step}
              </div>
              <div className="flex-1">
                <p className="text-white text-sm font-medium">{item.title}</p>
                <p className="text-gray-400 text-xs mt-0.5">{item.description}</p>
                {item.estimated_timeline && (
                  <p className="text-gray-500 text-xs mt-1">
                    <i className="fas fa-clock mr-1" /> {item.estimated_timeline}
                  </p>
                )}
              </div>
              {item.action_required && item.action_label && (
                <button className="text-xs border border-gold/40 text-gold px-2 py-1 rounded hover:bg-gold/10 transition-colors flex-shrink-0">
                  {item.action_label}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <p className="text-gray-600 text-xs mt-4">
        <i className="fas fa-info-circle mr-1" />
        This roadmap is informational only and does not constitute legal advice.
      </p>
    </div>
  );
};

const EligibilityInsightsPanel = ({ insights }) => {
  if (!insights || insights.length === 0) return null;

  const confidenceStyle = {
    high: 'bg-green-500/20 text-green-400 border-green-500/30',
    medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    low: 'bg-gray-700/50 text-gray-400 border-gray-600/30',
  };

  return (
    <div className="bg-gray-900 border border-gold/20 rounded-xl p-6 mb-6">
      <h3 className="text-white font-semibold text-lg mb-1 flex items-center gap-2">
        <i className="fas fa-lightbulb text-gold" /> Pathway Eligibility Insights
      </h3>
      <p className="text-gray-400 text-sm mb-5">
        Based on your profile, here are the immigration pathways most likely to apply to you.
      </p>
      <div className="space-y-3">
        {insights.map(insight => (
          <div key={insight.pathway_id} className="border border-gold/10 bg-[#303035]/30 rounded-lg p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-white text-sm font-medium">{insight.pathway_name}</span>
              <span className={`text-xs px-2 py-0.5 rounded border ${confidenceStyle[insight.confidence] || confidenceStyle.medium}`}>
                {insight.confidence} confidence
              </span>
            </div>
            <p className="text-gray-400 text-xs">{insight.reason}</p>
            {insight.recommended_action && (
              <p className="text-gold text-xs mt-1.5">
                <i className="fas fa-arrow-right mr-1" /> {insight.recommended_action}
              </p>
            )}
          </div>
        ))}
      </div>
      <p className="text-gray-600 text-xs mt-4">
        <i className="fas fa-info-circle mr-1" />
        Eligibility insights are not legal advice. Consult an immigration attorney for case-specific guidance.
      </p>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const VISA_TYPES = [
  { value: 'H-1B', label: 'H-1B (Specialty Occupation)' },
  { value: 'H-4', label: 'H-4 (H-1B Dependent)' },
  { value: 'L-1', label: 'L-1 (Intracompany Transfer)' },
  { value: 'O-1', label: 'O-1 (Extraordinary Ability)' },
  { value: 'F-1', label: 'F-1 (Student)' },
  { value: 'OPT', label: 'OPT / STEM OPT' },
  { value: 'J-1', label: 'J-1 (Exchange Visitor)' },
  { value: 'B-1/B-2', label: 'B-1/B-2 (Visitor)' },
  { value: 'TN', label: 'TN (NAFTA/USMCA Professional)' },
  { value: 'E-3', label: 'E-3 (Australian Specialty)' },
  { value: 'Green Card', label: 'Permanent Resident (Green Card)' },
  { value: 'US Citizen', label: 'US Citizen' },
  { value: 'Asylum', label: 'Asylee / Refugee' },
  { value: 'DACA', label: 'DACA' },
  { value: 'Unlawful Presence', label: 'Unlawful Presence / Undocumented' },
  { value: 'Other', label: 'Other' },
];

const DEGREE_TYPES = [
  { value: "Bachelor's", label: "Bachelor's Degree (BS/BA)" },
  { value: "Master's", label: "Master's Degree (MS/MA/MBA)" },
  { value: 'PhD', label: 'PhD / Doctoral Degree' },
  { value: 'Professional', label: 'Professional Degree (MD/JD/DDS)' },
  { value: 'Associate', label: "Associate's Degree" },
  { value: 'Diploma', label: 'High School Diploma / GED' },
  { value: 'Other', label: 'Other' },
];

const IMMIGRATION_GOALS = [
  { value: 'green card', label: 'Permanent Resident (Green Card)' },
  { value: 'citizenship', label: 'US Citizenship' },
  { value: 'employment', label: 'Work Authorization / Employment-Based' },
  { value: 'study', label: 'Student Visa' },
  { value: 'family reunification', label: 'Family Reunification' },
  { value: 'asylum', label: 'Asylum / Humanitarian Protection' },
  { value: 'change of status', label: 'Change of Status' },
  { value: 'other', label: 'Other' },
];

const ImmigrationProfile = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Profile state
  const [profile, setProfile] = useState(null);
  const [roadmap, setRoadmap] = useState(null);
  const [eligibility, setEligibility] = useState(null);
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    current_visa_type: '',
    visa_expiry_date: '',
    entry_date: '',
    country_of_birth: '',
    country_of_citizenship: '',
    immigration_goals: '',
    priority_goal: '',
  });

  // Employment entry
  const [employment, setEmployment] = useState({
    employer: '',
    role: '',
    start_date: '',
    end_date: '',
    is_current: true,
    sponsorship: '',
    location: '',
  });

  // Education entry
  const [education, setEducation] = useState({
    institution: '',
    degree: '',
    field_of_study: '',
    graduation_year: '',
    country: '',
    is_us_institution: false,
  });

  // Family
  const [familyComposition, setFamilyComposition] = useState({
    spouse_us_citizen: false,
    spouse_immigration_status: '',
    has_dependents: false,
    number_of_dependents: '',
  });

  // Prior filing
  const [priorFiling, setPriorFiling] = useState({
    form: '',
    filing_date: '',
    outcome: '',
    service_center: '',
    rfe_received: false,
  });

  // Lists (multiple entries)
  const [employmentList, setEmploymentList] = useState([]);
  const [educationList, setEducationList] = useState([]);
  const [priorFilingsList, setPriorFilingsList] = useState([]);

  // UI state
  const [activeSection, setActiveSection] = useState('status');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState('');
  const [loadingRoadmap, setLoadingRoadmap] = useState(false);
  const [loadingEligibility, setLoadingEligibility] = useState(false);

  // ── Load existing profile ────────────────────────────────────────────────
  useEffect(() => {
    const loadProfile = async () => {
      setLoading(true);
      try {
        const data = await apiFetch('/api/profile');
        setProfile(data);
        // Pre-populate form
        setFormData({
          current_visa_type: data.current_visa_type || '',
          visa_expiry_date: data.visa_expiry_date ? data.visa_expiry_date.split('T')[0] : '',
          entry_date: data.entry_date ? data.entry_date.split('T')[0] : '',
          country_of_birth: data.country_of_birth || '',
          country_of_citizenship: data.country_of_citizenship || '',
          immigration_goals: data.immigration_goals || '',
          priority_goal: data.priority_goal || '',
        });
        if (data.employment_history?.length) setEmploymentList(data.employment_history);
        if (data.education_history?.length) setEducationList(data.education_history);
        if (data.family_composition && Object.keys(data.family_composition).length)
          setFamilyComposition(data.family_composition);
        if (data.prior_filings?.length) setPriorFilingsList(data.prior_filings);
      } catch (err) {
        if (err.status !== 404) {
          setError(err.message);
        }
        // 404 = no profile yet, that's fine
      } finally {
        setLoading(false);
      }
    };
    loadProfile();
  }, []);

  // ── Load roadmap & eligibility after profile loads ───────────────────────
  useEffect(() => {
    if (profile) {
      fetchRoadmap();
      fetchEligibility();
    }
  }, [profile?.version]); // re-fetch when version changes

  const fetchRoadmap = async () => {
    setLoadingRoadmap(true);
    try {
      const data = await apiFetch('/api/profile/roadmap');
      setRoadmap(data);
    } catch (err) {
      console.warn('Roadmap fetch failed:', err.message);
    } finally {
      setLoadingRoadmap(false);
    }
  };

  const fetchEligibility = async () => {
    setLoadingEligibility(true);
    try {
      const data = await apiFetch('/api/profile/eligibility');
      setEligibility(data);
    } catch (err) {
      console.warn('Eligibility fetch failed:', err.message);
    } finally {
      setLoadingEligibility(false);
    }
  };

  const fetchVersions = async () => {
    try {
      const data = await apiFetch('/api/profile/versions');
      setVersions(data);
    } catch (err) {
      console.warn('Versions fetch failed:', err.message);
    }
  };

  // ── Save helper ──────────────────────────────────────────────────────────
  const saveProfile = async (patchData) => {
    setSaving(true);
    setError('');
    try {
      let saved;
      if (!profile) {
        // First time — create
        saved = await apiFetch('/api/profile', {
          method: 'POST',
          body: JSON.stringify(patchData),
        });
      } else {
        // Subsequent — patch
        saved = await apiFetch('/api/profile', {
          method: 'PATCH',
          body: JSON.stringify(patchData),
        });
      }
      setProfile(saved);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Section: Immigration Status ─────────────────────────────────────────
  const saveStatusSection = () =>
    saveProfile({
      current_visa_type: formData.current_visa_type || undefined,
      visa_expiry_date: formData.visa_expiry_date || undefined,
      entry_date: formData.entry_date || undefined,
      country_of_birth: formData.country_of_birth || undefined,
      country_of_citizenship: formData.country_of_citizenship || undefined,
    });

  // ── Section: Employment ─────────────────────────────────────────────────
  const addEmploymentEntry = () => {
    if (!employment.employer && !employment.role) return;
    const newList = [...employmentList, { ...employment }];
    setEmploymentList(newList);
    setEmployment({ employer: '', role: '', start_date: '', end_date: '', is_current: true, sponsorship: '', location: '' });
    saveProfile({ employment_history: newList });
  };

  const removeEmploymentEntry = (idx) => {
    const newList = employmentList.filter((_, i) => i !== idx);
    setEmploymentList(newList);
    saveProfile({ employment_history: newList });
  };

  // ── Section: Education ──────────────────────────────────────────────────
  const addEducationEntry = () => {
    if (!education.institution && !education.degree) return;
    const newList = [...educationList, { ...education, graduation_year: education.graduation_year ? parseInt(education.graduation_year) : null }];
    setEducationList(newList);
    setEducation({ institution: '', degree: '', field_of_study: '', graduation_year: '', country: '', is_us_institution: false });
    saveProfile({ education_history: newList });
  };

  const removeEducationEntry = (idx) => {
    const newList = educationList.filter((_, i) => i !== idx);
    setEducationList(newList);
    saveProfile({ education_history: newList });
  };

  // ── Section: Family ─────────────────────────────────────────────────────
  const saveFamilySection = () =>
    saveProfile({ family_composition: familyComposition });

  // ── Section: Prior Filings ──────────────────────────────────────────────
  const addPriorFiling = () => {
    if (!priorFiling.form) return;
    const newList = [...priorFilingsList, { ...priorFiling }];
    setPriorFilingsList(newList);
    setPriorFiling({ form: '', filing_date: '', outcome: '', service_center: '', rfe_received: false });
    saveProfile({ prior_filings: newList });
  };

  const removePriorFiling = (idx) => {
    const newList = priorFilingsList.filter((_, i) => i !== idx);
    setPriorFilingsList(newList);
    saveProfile({ prior_filings: newList });
  };

  // ── Section: Goals ──────────────────────────────────────────────────────
  const saveGoalsSection = () =>
    saveProfile({
      immigration_goals: formData.immigration_goals || undefined,
      priority_goal: formData.priority_goal || undefined,
    });

  // ── Helpers ──────────────────────────────────────────────────────────────
  const completionPct = profile?.completion_percentage ?? 0;

  const sectionComplete = {
    status: !!(formData.current_visa_type && formData.country_of_birth),
    employment: employmentList.length > 0,
    education: educationList.length > 0,
    family: !!(familyComposition.spouse_immigration_status || familyComposition.spouse_us_citizen),
    filings: priorFilingsList.length > 0,
    goals: !!formData.immigration_goals,
  };

  const sections = [
    { id: 'status', label: 'Immigration Status', icon: '🛂' },
    { id: 'employment', label: 'Employment', icon: '💼' },
    { id: 'education', label: 'Education', icon: '🎓' },
    { id: 'family', label: 'Family', icon: '👨‍👩‍👧‍👦' },
    { id: 'filings', label: 'Prior Filings', icon: '📋' },
    { id: 'goals', label: 'Immigration Goals', icon: '🎯' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#3A3A3F] flex items-center justify-center">
        <div className="text-gold text-xl">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#3A3A3F] text-white">
      {/* Header */}
      <div className="bg-gray-950 border-b border-gold/20 px-6 py-4 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <i className="fas fa-arrow-left mr-2" />
            Dashboard
          </button>
          <span className="text-gray-600">|</span>
          <h1 className="text-white font-semibold text-lg">
            <i className="fas fa-user-circle text-gold mr-2" />
            Immigration Profile
          </h1>
        </div>
        <div className="flex items-center gap-3">
          {saveSuccess && (
            <span className="text-green-400 text-sm flex items-center gap-1">
              <i className="fas fa-check" /> Saved
            </span>
          )}
          {saving && (
            <span className="text-gray-400 text-sm flex items-center gap-1">
              <i className="fas fa-spinner fa-spin" /> Saving...
            </span>
          )}
          <button
            onClick={() => { fetchRoadmap(); fetchEligibility(); }}
            className="text-xs border border-gold/40 text-gold px-3 py-1.5 rounded hover:bg-gold/10 transition-colors"
          >
            <i className="fas fa-sync-alt mr-1" /> Refresh Insights
          </button>
          <PurchaseNavButton />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {error && (
          <div className="bg-red-900/30 border border-red-500/40 rounded-lg px-4 py-3 mb-6 text-red-300 text-sm">
            <i className="fas fa-exclamation-triangle mr-2" />{error}
          </div>
        )}

        {/* Profile headline */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-white mb-1">
            Your Immigration Profile
          </h2>
          <p className="text-gray-400">
            Your profile is free and always available. Complete it once — we use it across all your pathways.
          </p>
        </div>

        {/* Completion Bar */}
        <ProgressBar percentage={completionPct} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* LEFT: Form sections */}
          <div className="lg:col-span-2">
            {/* Section Navigator */}
            <div className="flex flex-wrap gap-2 mb-6">
              {sections.map(s => (
                <button
                  key={s.id}
                  onClick={() => setActiveSection(s.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5
                    ${activeSection === s.id
                      ? 'bg-gray-800 border-2 border-gold text-white'
                      : sectionComplete[s.id]
                        ? 'bg-green-900/40 border border-green-500/40 text-green-400'
                        : 'bg-gray-900 border border-gray-700 text-gray-400 hover:text-white hover:border-gold'
                    }`}
                >
                  <span>{s.icon}</span> {s.label}
                  {sectionComplete[s.id] && activeSection !== s.id && (
                    <i className="fas fa-check text-xs" />
                  )}
                </button>
              ))}
            </div>

            {/* ── Immigration Status Section ── */}
            {activeSection === 'status' && (
              <SectionCard title="Current Immigration Status" icon="🛂" completed={sectionComplete.status}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                  <SelectField
                    label="Current Visa / Status"
                    value={formData.current_visa_type}
                    onChange={v => setFormData(f => ({ ...f, current_visa_type: v }))}
                    options={VISA_TYPES}
                    placeholder="Select visa type..."
                  />
                  <InputField
                    label="Visa / Status Expiry Date"
                    value={formData.visa_expiry_date}
                    onChange={v => setFormData(f => ({ ...f, visa_expiry_date: v }))}
                    type="date"
                    placeholder=""
                  />
                  <InputField
                    label="Date of Entry to US"
                    value={formData.entry_date}
                    onChange={v => setFormData(f => ({ ...f, entry_date: v }))}
                    type="date"
                    placeholder=""
                  />
                  <InputField
                    label="Country of Birth"
                    value={formData.country_of_birth}
                    onChange={v => setFormData(f => ({ ...f, country_of_birth: v }))}
                    placeholder="e.g. India"
                    required
                  />
                  <InputField
                    label="Country of Citizenship"
                    value={formData.country_of_citizenship}
                    onChange={v => setFormData(f => ({ ...f, country_of_citizenship: v }))}
                    placeholder="e.g. India"
                    required
                  />
                </div>
                <button
                  onClick={saveStatusSection}
                  disabled={saving}
                  className="mt-2 bg-gold text-white px-6 py-2.5 rounded-lg font-semibold text-sm hover:bg-gold/80 transition-colors disabled:opacity-50 shadow-md"
                >
                  {saving ? 'Saving...' : 'Save Section'}
                </button>
              </SectionCard>
            )}

            {/* ── Employment Section ── */}
            {activeSection === 'employment' && (
              <SectionCard title="Employment History" icon="💼" completed={sectionComplete.employment}>
                {/* Existing entries */}
                {employmentList.map((emp, idx) => (
                  <div key={idx} className="bg-[#303035]/40 border border-gold/10 rounded-lg p-3 mb-3 flex items-center justify-between">
                    <div>
                      <p className="text-white text-sm font-medium">{emp.employer || 'Unknown employer'}</p>
                      <p className="text-gray-400 text-xs">{emp.role} {emp.is_current ? '(Current)' : ''}</p>
                      {emp.sponsorship && <p className="text-gray-500 text-xs">Sponsorship: {emp.sponsorship}</p>}
                    </div>
                    <button onClick={() => removeEmploymentEntry(idx)} className="text-red-400 hover:text-red-300 ml-4">
                      <i className="fas fa-times" />
                    </button>
                  </div>
                ))}

                <div className="border border-dashed border-gold/20 rounded-lg p-4 mt-2">
                  <p className="text-gray-400 text-sm mb-3 font-medium">Add Employment</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                    <InputField label="Employer Name" value={employment.employer} onChange={v => setEmployment(e => ({ ...e, employer: v }))} placeholder="e.g. Acme Corp" />
                    <InputField label="Job Title / Role" value={employment.role} onChange={v => setEmployment(e => ({ ...e, role: v }))} placeholder="e.g. Software Engineer" />
                    <InputField label="Start Date" value={employment.start_date} onChange={v => setEmployment(e => ({ ...e, start_date: v }))} type="date" />
                    <InputField label="End Date (leave blank if current)" value={employment.end_date} onChange={v => setEmployment(e => ({ ...e, end_date: v }))} type="date" />
                    <InputField label="Location / City" value={employment.location} onChange={v => setEmployment(e => ({ ...e, location: v }))} placeholder="e.g. San Francisco, CA" />
                    <SelectField
                      label="Visa Sponsorship"
                      value={employment.sponsorship}
                      onChange={v => setEmployment(e => ({ ...e, sponsorship: v }))}
                      options={[
                        { value: 'H-1B', label: 'H-1B' },
                        { value: 'L-1', label: 'L-1' },
                        { value: 'OPT', label: 'OPT / STEM OPT' },
                        { value: 'Green Card', label: 'Green Card / Permanent Resident' },
                        { value: 'US Citizen', label: 'US Citizen (no sponsorship needed)' },
                        { value: 'none', label: 'No sponsorship' },
                        { value: 'pending', label: 'Pending sponsorship' },
                      ]}
                    />
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    <input
                      type="checkbox"
                      id="is_current"
                      checked={employment.is_current}
                      onChange={e => setEmployment(emp => ({ ...emp, is_current: e.target.checked }))}
                      className="text-gold"
                    />
                    <label htmlFor="is_current" className="text-gray-400 text-sm">This is my current employer</label>
                  </div>
                  <button
                    onClick={addEmploymentEntry}
                    className="bg-gold/20 border border-gold/40 text-gold px-4 py-2 rounded-lg text-sm hover:bg-gold/30 transition-colors"
                  >
                    <i className="fas fa-plus mr-1" /> Add Entry
                  </button>
                </div>
              </SectionCard>
            )}

            {/* ── Education Section ── */}
            {activeSection === 'education' && (
              <SectionCard title="Education History" icon="🎓" completed={sectionComplete.education}>
                {educationList.map((edu, idx) => (
                  <div key={idx} className="bg-[#303035]/40 border border-gold/10 rounded-lg p-3 mb-3 flex items-center justify-between">
                    <div>
                      <p className="text-white text-sm font-medium">{edu.degree} — {edu.field_of_study}</p>
                      <p className="text-gray-400 text-xs">{edu.institution} {edu.graduation_year ? `(${edu.graduation_year})` : ''}</p>
                      {edu.is_us_institution && <span className="text-xs text-blue-400">US Institution</span>}
                    </div>
                    <button onClick={() => removeEducationEntry(idx)} className="text-red-400 hover:text-red-300 ml-4">
                      <i className="fas fa-times" />
                    </button>
                  </div>
                ))}

                <div className="border border-dashed border-gold/20 rounded-lg p-4 mt-2">
                  <p className="text-gray-400 text-sm mb-3 font-medium">Add Education</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                    <InputField label="Institution / University" value={education.institution} onChange={v => setEducation(e => ({ ...e, institution: v }))} placeholder="e.g. MIT" />
                    <SelectField label="Degree Type" value={education.degree} onChange={v => setEducation(e => ({ ...e, degree: v }))} options={DEGREE_TYPES} placeholder="Select degree..." />
                    <InputField label="Field of Study" value={education.field_of_study} onChange={v => setEducation(e => ({ ...e, field_of_study: v }))} placeholder="e.g. Computer Science" />
                    <InputField label="Graduation Year" value={education.graduation_year} onChange={v => setEducation(e => ({ ...e, graduation_year: v }))} placeholder="e.g. 2018" />
                    <InputField label="Country" value={education.country} onChange={v => setEducation(e => ({ ...e, country: v }))} placeholder="e.g. United States" />
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    <input type="checkbox" id="is_us_inst" checked={education.is_us_institution} onChange={e => setEducation(edu => ({ ...edu, is_us_institution: e.target.checked }))} />
                    <label htmlFor="is_us_inst" className="text-gray-400 text-sm">US accredited institution</label>
                  </div>
                  <button
                    onClick={addEducationEntry}
                    className="bg-gold/20 border border-gold/40 text-gold px-4 py-2 rounded-lg text-sm hover:bg-gold/30 transition-colors"
                  >
                    <i className="fas fa-plus mr-1" /> Add Entry
                  </button>
                </div>
              </SectionCard>
            )}

            {/* ── Family Section ── */}
            {activeSection === 'family' && (
              <SectionCard title="Family Composition" icon="👨‍👩‍👧‍👦" completed={sectionComplete.family}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                  <SelectField
                    label="Spouse / Partner Immigration Status"
                    value={familyComposition.spouse_immigration_status}
                    onChange={v => setFamilyComposition(f => ({ ...f, spouse_immigration_status: v }))}
                    options={VISA_TYPES}
                    placeholder="No spouse / not applicable"
                  />
                  <div className="mb-4">
                    <label className="block text-gray-400 text-sm mb-1">Number of Dependents</label>
                    <input
                      type="number"
                      min="0"
                      value={familyComposition.number_of_dependents || ''}
                      onChange={e => setFamilyComposition(f => ({ ...f, number_of_dependents: e.target.value }))}
                      className="w-full bg-[#303035]/40 border border-gold/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-gold/60"
                      placeholder="0"
                    />
                  </div>
                </div>
                <div className="flex flex-wrap gap-4 mb-5">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={familyComposition.spouse_us_citizen || false}
                      onChange={e => setFamilyComposition(f => ({ ...f, spouse_us_citizen: e.target.checked }))}
                    />
                    <span className="text-gray-400 text-sm">Spouse is US Citizen</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={familyComposition.has_dependents || false}
                      onChange={e => setFamilyComposition(f => ({ ...f, has_dependents: e.target.checked }))}
                    />
                    <span className="text-gray-400 text-sm">I have dependents (children)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={familyComposition.us_citizen_parent || false}
                      onChange={e => setFamilyComposition(f => ({ ...f, us_citizen_parent: e.target.checked }))}
                    />
                    <span className="text-gray-400 text-sm">I have a US Citizen parent</span>
                  </label>
                </div>
                <button
                  onClick={saveFamilySection}
                  disabled={saving}
                  className="bg-gold text-white px-6 py-2.5 rounded-lg font-semibold text-sm hover:bg-gold/80 transition-colors disabled:opacity-50 shadow-md"
                >
                  {saving ? 'Saving...' : 'Save Section'}
                </button>
              </SectionCard>
            )}

            {/* ── Prior Filings Section ── */}
            {activeSection === 'filings' && (
              <SectionCard title="Prior US Immigration Filings" icon="📋" completed={sectionComplete.filings}>
                <p className="text-gray-500 text-sm mb-4">Include any USCIS forms you have previously filed (I-130, I-140, I-485, etc.)</p>

                {priorFilingsList.map((filing, idx) => (
                  <div key={idx} className="bg-[#303035]/40 border border-gold/10 rounded-lg p-3 mb-3 flex items-center justify-between">
                    <div>
                      <p className="text-white text-sm font-medium">{filing.form}</p>
                      <p className="text-gray-400 text-xs">
                        {filing.filing_date} · {filing.outcome} {filing.rfe_received ? '· RFE received' : ''}
                      </p>
                    </div>
                    <button onClick={() => removePriorFiling(idx)} className="text-red-400 hover:text-red-300 ml-4">
                      <i className="fas fa-times" />
                    </button>
                  </div>
                ))}

                <div className="border border-dashed border-gold/20 rounded-lg p-4 mt-2">
                  <p className="text-gray-400 text-sm mb-3 font-medium">Add Filing</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                    <InputField label="Form Number" value={priorFiling.form} onChange={v => setPriorFiling(f => ({ ...f, form: v }))} placeholder="e.g. I-140" />
                    <InputField label="Filing Date" value={priorFiling.filing_date} onChange={v => setPriorFiling(f => ({ ...f, filing_date: v }))} type="date" />
                    <SelectField
                      label="Outcome"
                      value={priorFiling.outcome}
                      onChange={v => setPriorFiling(f => ({ ...f, outcome: v }))}
                      options={[
                        { value: 'approved', label: 'Approved' },
                        { value: 'pending', label: 'Pending' },
                        { value: 'denied', label: 'Denied' },
                        { value: 'withdrawn', label: 'Withdrawn' },
                        { value: 'rfe_received', label: 'RFE Received' },
                      ]}
                    />
                    <SelectField
                      label="Service Center"
                      value={priorFiling.service_center}
                      onChange={v => setPriorFiling(f => ({ ...f, service_center: v }))}
                      options={[
                        { value: 'Nebraska', label: 'Nebraska Service Center' },
                        { value: 'Texas', label: 'Texas Service Center' },
                        { value: 'California', label: 'California Service Center' },
                        { value: 'Vermont', label: 'Vermont Service Center' },
                        { value: 'Online', label: 'USCIS Online' },
                      ]}
                    />
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    <input type="checkbox" id="rfe" checked={priorFiling.rfe_received} onChange={e => setPriorFiling(f => ({ ...f, rfe_received: e.target.checked }))} />
                    <label htmlFor="rfe" className="text-gray-400 text-sm">Request for Evidence (RFE) was received on this filing</label>
                  </div>
                  <button
                    onClick={addPriorFiling}
                    className="bg-gold/20 border border-gold/40 text-gold px-4 py-2 rounded-lg text-sm hover:bg-gold/30 transition-colors"
                  >
                    <i className="fas fa-plus mr-1" /> Add Filing
                  </button>
                </div>
              </SectionCard>
            )}

            {/* ── Goals Section ── */}
            {activeSection === 'goals' && (
              <SectionCard title="Immigration Goals" icon="🎯" completed={sectionComplete.goals}>
                <SelectField
                  label="Primary Immigration Goal"
                  value={formData.immigration_goals}
                  onChange={v => setFormData(f => ({ ...f, immigration_goals: v }))}
                  options={IMMIGRATION_GOALS}
                  placeholder="What do you want to achieve?"
                />
                <SelectField
                  label="What matters most to you?"
                  value={formData.priority_goal}
                  onChange={v => setFormData(f => ({ ...f, priority_goal: v }))}
                  options={[
                    { value: 'speed', label: 'Speed — I want the fastest path' },
                    { value: 'stability', label: 'Stability — I want permanent status ASAP' },
                    { value: 'family', label: 'Family — Keeping family together is priority' },
                    { value: 'career', label: 'Career — I want to advance my career in the US' },
                    { value: 'education', label: 'Education — I want to study in the US' },
                  ]}
                  placeholder="Select priority..."
                />
                <button
                  onClick={saveGoalsSection}
                  disabled={saving}
                  className="mt-2 bg-gold text-white px-6 py-2.5 rounded-lg font-semibold text-sm hover:bg-gold/80 transition-colors disabled:opacity-50 shadow-md"
                >
                  {saving ? 'Saving...' : 'Save Section'}
                </button>
              </SectionCard>
            )}

            {/* Version History toggle */}
            <div className="mt-6">
              <button
                onClick={async () => { setShowVersions(!showVersions); if (!showVersions) await fetchVersions(); }}
                className="text-gray-500 text-sm hover:text-gray-300 transition-colors"
              >
                <i className="fas fa-history mr-2" />
                {showVersions ? 'Hide' : 'Show'} Profile Change History
              </button>
              {showVersions && versions.length > 0 && (
                <div className="mt-3 space-y-2">
                  {versions.map(v => (
                    <div key={v.id} className="bg-gray-900/50 border border-gold/10 rounded-lg px-4 py-3">
                      <div className="flex items-center justify-between">
                        <span className="text-gold text-sm font-medium">Version {v.version}</span>
                        <span className="text-gray-500 text-xs">{new Date(v.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {showVersions && versions.length === 0 && (
                <p className="text-gray-600 text-sm mt-2">No version history yet.</p>
              )}
            </div>
          </div>

          {/* RIGHT: Insights sidebar */}
          <div className="lg:col-span-1">
            {/* Profile summary card */}
            <div className="bg-gray-900 border border-gold/20 rounded-xl p-5 mb-5">
              <h3 className="text-white font-semibold mb-3">
                <i className="fas fa-user text-gold mr-2" /> Profile Summary
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Status</span>
                  <span className="text-white">{formData.current_visa_type || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Country of Birth</span>
                  <span className="text-white">{formData.country_of_birth || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Employment entries</span>
                  <span className="text-white">{employmentList.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Education entries</span>
                  <span className="text-white">{educationList.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Prior filings</span>
                  <span className="text-white">{priorFilingsList.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Profile version</span>
                  <span className="text-gold">v{profile?.version || 1}</span>
                </div>
              </div>
            </div>

            {/* Free tier note */}
            <div className="bg-gold/5 border border-gold/20 rounded-xl p-4 mb-5">
              <p className="text-gold text-xs font-semibold mb-1">
                <i className="fas fa-lock-open mr-1" /> Always Free
              </p>
              <p className="text-gray-400 text-xs">
                Your immigration profile is permanently free. It's never deleted, even if a pathway entitlement lapses.
              </p>
            </div>

            {/* Roadmap panel */}
            {loadingRoadmap ? (
              <div className="bg-gray-900 border border-gold/20 rounded-xl p-5 mb-5 text-center">
                <i className="fas fa-spinner fa-spin text-gold" />
                <p className="text-gray-400 text-sm mt-2">Generating roadmap...</p>
              </div>
            ) : (
              <RoadmapPanel roadmap={roadmap} />
            )}

            {/* Eligibility insights */}
            {loadingEligibility ? (
              <div className="bg-gray-900 border border-gold/20 rounded-xl p-5 mb-5 text-center">
                <i className="fas fa-spinner fa-spin text-gold" />
                <p className="text-gray-400 text-sm mt-2">Evaluating eligibility...</p>
              </div>
            ) : (
              eligibility && <EligibilityInsightsPanel insights={eligibility.insights} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImmigrationProfile;
